
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
// Import from our custom wrapper instead of directly from sonner
import { toast } from "@/utils/sonner-toast-wrapper";

interface UseGuestAvatarUploadProps {
  inviteId: string;
  eventId: string;
  onAvatarChange: (url: string) => void;
}

export const useGuestAvatarUpload = ({ inviteId, eventId, onAvatarChange }: UseGuestAvatarUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (file: File) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast("Invalid file type", {
        description: "Please upload an image file"
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast("File too large", {
        description: "Image must be less than 5MB"
      });
      return;
    }

    setIsUploading(true);
    try {
      // Upload new avatar with event/invite IDs as folder name
      const fileExt = file.name.split('.').pop();
      const fileName = `events/${eventId}/invites/${inviteId}/${crypto.randomUUID()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      onAvatarChange(publicUrl);
      
      toast("Success", {
        description: "Selfie uploaded successfully"
      });
    } catch (error) {
      console.error('Error uploading selfie:', error);
      toast("Error", {
        description: "Failed to upload selfie. Please try again."
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkipSelfie = () => {
    onAvatarChange('');
  };

  return {
    isUploading,
    handleImageUpload,
    handleSkipSelfie
  };
};
